import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { VerticalScrollableContentWithArrows } from "@/components/ui/vertical-scrollable-content-with-arrows";
import { StepProps } from "@/lib/builder/models/builder.model";
import { ItineraryTotals } from "./models/itinerary.model";
import { ItineraryBuilder } from "./models/itinerary-builder.model";
import { axisFromItinerary } from "./models/day-axis";
import { Calendar, CarTaxiFront, FerrisWheel, CupSoda, MicVocal, FileText, Bed, Plane, Edit, X, Save } from "lucide-react";
import { ItineraryActivity } from "./models/activity.model";
import { useI18n } from '@/hooks/useI18n';
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PaymentPlanCard } from "./components/payment-plan/PaymentPlanCard";
import { useBuilderCapabilities } from "./capabilities/BuilderCapabilitiesContext";

const CheckoutStep = ({
    data,
    onDataChange,
    builder,
    setCanProceed,
}: StepProps) => {
    // Chi vede markup, policy e piano rate lo decidono le capacita' del flusso, non il
    // ruolo letto qui: e' la stessa regola di footer e card.
    const { showPricing, canMarkInstallmentPaid } = useBuilderCapabilities();
    const { t } = useI18n();

    const DEFAULT_P2B_COMMISSION_PERCENTAGE = 20;

    const [commissionMode, setCommissionMode] = useState<'%' | '€'>(
        data.p2b_commission_type === 'fixed' ? '€' : '%'
    );
    const [commissionInput, setCommissionInput] = useState('');
    const [isDirtyCommission, setIsDirtyCommission] = useState(false);

    const [totals, setTotals] = useState<ItineraryTotals>({
        commisionable_total: 0,
        not_commisionable_total: 0,
        p2b_commission_percentage: DEFAULT_P2B_COMMISSION_PERCENTAGE,
        td_fee_percentage: 97,
        custom_fee: 0,
        p2b_markup_gross: 0,
        vat_amount: 0,
        p2b_markup_net: 0,
        td_fee_amount: 0,
        p2b_fee_amount: 0,
        total: 0,
    });

    const itineraryBuilder = useMemo(() => {
        if (builder) return builder;

        if (data?.start_date && data?.end_date && data?.itinerary_days) {
            const newBuilder = new ItineraryBuilder();
            newBuilder.loadFromArray(data.itinerary_days, axisFromItinerary(data));
            return newBuilder;
        }
        return null;
    }, [builder, data]);

    useEffect(() => {
        setTotals(data.extractTotals());
    }, [data]);

    useEffect(() => {
        if (!isDirtyCommission) {
            setCommissionMode(data.p2b_commission_type === 'fixed' ? '€' : '%');
        }
    }, [data.p2b_commission_type]);

    useEffect(() => {
        setCanProceed(true);
    }, []);

    const formatPrice = (value: number | null | undefined): string => {
        if (value == null) return '-';
        return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const getActivityCost = (activity: ItineraryActivity): number => {
        return (activity as { cost?: number }).cost || 0;
    };

    const isChargeable = (activity: ItineraryActivity): boolean => {
        return activity.include_price && getActivityCost(activity) > 0;
    };

    const shouldShowInCheckout = (activity: ItineraryActivity): boolean => {
        if (activity.type === 'accommodation' && (activity as any).inOrOutActivity === 'out') {
            return false;
        }
        return getActivityCost(activity) > 0;
    };

    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    // Value currently displayed in the input (derived from saved data when clean, from state when dirty)
    const displayedCommissionValue = isDirtyCommission
        ? commissionInput
        : commissionMode === '€'
            ? (data.p2b_commission_type === 'fixed'
                ? String(data.p2b_commission_fixed_amount)
                : totals.p2b_markup_gross.toFixed(2))
            : String(totals.p2b_commission_percentage);

    // Base di calcolo del markup: tutto ciò che il cliente paga (voli inclusi),
    // non solo la quota commissionabile. Deve restare allineata a extractTotals().
    const servicesTotal = (totals.commisionable_total || 0) + (totals.not_commisionable_total || 0);

    const handleToggleMode = (mode: '%' | '€') => {
        if (mode === commissionMode) return;
        if (isDirtyCommission) {
            const currentNum = parseFloat(commissionInput.replace(',', '.'));
            const ct = servicesTotal;
            if (mode === '€') {
                const euroVal = !isNaN(currentNum) && ct > 0 ? (currentNum / 100) * ct : totals.p2b_markup_gross;
                setCommissionInput(euroVal.toFixed(2));
            } else {
                const pctVal = !isNaN(currentNum) && ct > 0 ? (currentNum / ct) * 100 : totals.p2b_commission_percentage;
                setCommissionInput(String(parseFloat(pctVal.toFixed(10))));
            }
        }
        setCommissionMode(mode);
    };

    const handleSaveCommission = () => {
        const numVal = parseFloat(commissionInput.replace(',', '.'));
        if (isNaN(numVal) || numVal < 0) return;
        const ct = servicesTotal;
        if (commissionMode === '%') {
            const pct = Math.min(100, numVal);
            const fixed = ct > 0 ? (ct * pct) / 100 : 0;
            onDataChange({
                p2b_commission_type: 'percentage',
                p2b_commission_percentage: Math.round(pct * 10000) / 10000,
                p2b_commission_fixed_amount: Math.round(fixed * 100) / 100,
            });
        } else {
            const fixed = numVal;
            const pct = ct > 0 ? (fixed / ct) * 100 : 0;
            onDataChange({
                p2b_commission_type: 'fixed',
                p2b_commission_percentage: Math.round(pct * 10000) / 10000,
                p2b_commission_fixed_amount: Math.round(fixed * 100) / 100,
            });
        }
        setIsDirtyCommission(false);
    };

    const getActivityIcon = (activity: ItineraryActivity) => {
        const type = activity.type;

        const iconMap = {
            'accommodation': { icon: Bed, color: 'text-gray-700' },
            'flight': { icon: Plane, color: 'text-gray-700' },
            'transportation': { icon: CarTaxiFront, color: 'text-gray-700' },
            'activity': { icon: FerrisWheel, color: 'text-gray-700' },
            'food-drinks': { icon: CupSoda, color: 'text-gray-700' },
            'events': { icon: MicVocal, color: 'text-gray-700' },
            'notes': { icon: FileText, color: 'text-gray-700' },
        };
        return iconMap[type] || { icon: FileText, color: 'text-gray-700' };
    };

    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [cancellationNotesDraft, setCancellationNotesDraft] = useState(data.cancellation_policy_notes || '');

    useEffect(() => {
        if (!isEditingNotes) {
            setCancellationNotesDraft(data.cancellation_policy_notes || '');
        }
    }, [data.cancellation_policy_notes, isEditingNotes]);

    const handleSaveNotes = () => {
        onDataChange({ cancellation_policy_notes: cancellationNotesDraft });
        setIsEditingNotes(false);
    };

    const handleCancelNotes = () => {
        setCancellationNotesDraft(data.cancellation_policy_notes || '');
        setIsEditingNotes(false);
    };

    const _displayedNum = parseFloat(displayedCommissionValue.replace(',', '.'));
    const _ct = servicesTotal;
    const liveApproxOther = !isNaN(_displayedNum) && _displayedNum >= 0 && _ct > 0
        ? commissionMode === '€'
            ? (_displayedNum / _ct) * 100
            : (_displayedNum / 100) * _ct
        : null;

    return (
        <div className="w-full h-full mx-auto p-5 flex items-start justify-center">
            <div className="flex flex-col sm:flex-row gap-6 w-full md:w-full lg:w-[90%] xl:w-[90%] h-full sm:items-start">
                {/* Colonna sinistra: elenco attività sopra, policy di cancellazione sotto.
                    La policy tiene un'altezza propria e non scorre: a comprimersi è la
                    lista delle attività, che si consulta. */}
                <div className="flex flex-col gap-6 w-full sm:w-[40%] md:w-[50%] lg:w-[60%] h-full min-h-0">

                {/* Left Card - Activities Breakdown */}
                <Card className="flex flex-col flex-1 min-h-0 w-full overflow-hidden relative">
                    <CardContent className="flex-1 min-h-0 p-0">
                        <VerticalScrollableContentWithArrows
                            viewportClassName="p-4"
                        >
                        <div className="space-y-2 pr-2">
                            {itineraryBuilder?.getAllDays().map((day) => {
                                const dayActivities = day.activities.filter(a => shouldShowInCheckout(a));

                                if (dayActivities.length === 0) return null;

                                return (
                                    <div key={day.key} className="space-y-2">
                                        <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                                            <Calendar className="w-4 h-4 text-gray-500" />
                                            <h6 className="text-gray-900">
                                                {day.date ? formatDate(day.date) : t('builder.dayNumber', { number: day.index })}
                                            </h6>
                                        </div>
                                        <div className="space-y-2 pl-6">
                                            {dayActivities.map((activity) => {
                                                const chargeable = isChargeable(activity);
                                                const { icon: Icon, color } = getActivityIcon(activity);

                                                return (
                                                    <div
                                                        key={activity.id}
                                                        className={`flex items-start justify-between ${!chargeable ? 'opacity-40' : ''}`}
                                                    >
                                                        <div className="flex items-start gap-2 flex-1">
                                                            <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${chargeable ? color : 'text-gray-400'}`} />
                                                            <div className="flex-1">
                                                                <span className={`${chargeable ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                                                                    {activity.title}
                                                                </span>
                                                                {!chargeable && (
                                                                    <span className="ml-2 text-sm text-gray-400 italic">
                                                                        (not charged)
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span className={`${chargeable ? 'text-gray-900' : 'text-gray-400'} ml-4 flex-shrink-0`}>
                                                            €{formatPrice(getActivityCost(activity))}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        </VerticalScrollableContentWithArrows>
                    </CardContent>
                </Card>

                {/* Policy di cancellazione: solo per chi vende, non per il viaggiatore
                    che consulta. Resta fissa in fondo alla colonna, senza scroll proprio. */}
                {showPricing && (
                    <Card className="flex-shrink-0 w-full">
                        <CardContent className="p-4 flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="cancellation-notes" className="text-sm font-medium text-gray-900">
                                    {t('checkout.cancellationPolicyNotes')}
                                </Label>
                                <div className="flex gap-2">
                                    {isEditingNotes ? (
                                        <>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={handleCancelNotes}
                                                className="flex items-center gap-1"
                                            >
                                                <X className="h-3 w-3" />
                                                {t('common.cancel')}
                                            </Button>
                                            <Button
                                                type="button"
                                                size="sm"
                                                onClick={handleSaveNotes}
                                                className="flex items-center gap-1"
                                            >
                                                <Save className="h-3 w-3" />
                                                {t('common.save')}
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => setIsEditingNotes(true)}
                                            className="flex items-center gap-1"
                                        >
                                            <Edit className="h-3 w-3" />
                                            {t('common.edit')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {isEditingNotes ? (
                                <Textarea
                                    id="cancellation-notes"
                                    value={cancellationNotesDraft}
                                    onChange={(e) => setCancellationNotesDraft(e.target.value)}
                                    placeholder={t('checkout.cancellationPolicyPlaceholder')}
                                    className="min-h-[80px] text-sm"
                                    autoFocus
                                />
                            ) : (
                                <div className="min-h-[80px] max-h-[80px] overflow-y-auto text-sm rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700 whitespace-pre-wrap cursor-default">
                                    {cancellationNotesDraft || (
                                        <span className="text-gray-400 italic">{t('checkout.cancellationPolicyPlaceholder')}</span>
                                    )}
                                </div>
                            )}
                            <p className="text-xs text-gray-500">
                                {t('checkout.cancellationPolicyHelper')}
                            </p>
                        </CardContent>
                    </Card>
                )}
                </div>

                {/* Colonna destra: totali e markup, poi il piano rate. Scorre tutta
                    insieme: le due card si leggono in sequenza, non in parallelo. */}
                <VerticalScrollableContentWithArrows
                    className="flex flex-col gap-6 w-full sm:w-[60%] md:w-[50%] lg:w-[40%] h-full overflow-y-auto min-h-0"
                    viewportClassName="flex flex-col gap-6"
                >

                {/* Revenue Breakdown Card */}
                <Card className="flex flex-col w-full flex-shrink-0">
                    <CardContent className="p-6 flex flex-col">
                        <div className="space-y-3">
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center justify-between">
                                    <span className="font-medium text-gray-900">{t('checkout.servicesCost')}</span>
                                    <span className="font-medium text-gray-900">
                                        €{formatPrice(servicesTotal)}
                                    </span>
                                </div>
                            </div>

                            {/* Advisor Fee */}
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="font-medium text-gray-900">{t('checkout.advisorFee')}</span>
                                    {showPricing ? (
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <div className="flex items-center gap-1 border border-gray-300 rounded-lg px-2 py-1 bg-white focus-within:border-gray-500 transition-all">
                                                <input
                                                    type="number"
                                                    value={displayedCommissionValue}
                                                    onChange={(e) => {
                                                        let val = e.target.value;
                                                        if (commissionMode === '€') {
                                                            const match = val.match(/^-?\d*\.?\d{0,2}/);
                                                            val = match ? match[0] : val;
                                                        }
                                                        setCommissionInput(val);
                                                        setIsDirtyCommission(true);
                                                    }}
                                                    className="w-20 text-base bg-transparent outline-none p-0 text-gray-900 text-right"
                                                    min={0}
                                                    max={commissionMode === '%' ? 100 : undefined}
                                                    step={0.1}
                                                />
                                                <span className="text-sm font-semibold text-gray-500">{commissionMode}</span>
                                            </div>
                                            <div className="flex rounded-full border border-gray-200 overflow-hidden flex-shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => handleToggleMode('%')}
                                                    className={`px-2.5 py-1 text-xs transition-colors ${commissionMode === '%' ? 'bg-gray-100 font-semibold text-black' : 'text-gray-500 hover:bg-gray-50'}`}
                                                >%</button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleToggleMode('€')}
                                                    className={`px-2.5 py-1 text-xs border-l border-gray-200 transition-colors ${commissionMode === '€' ? 'bg-gray-100 font-semibold text-black' : 'text-gray-500 hover:bg-gray-50'}`}
                                                >€</button>
                                            </div>
                                            {isDirtyCommission && (
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    onClick={handleSaveCommission}
                                                    className="h-7 px-2 flex items-center justify-center flex-shrink-0"
                                                >
                                                    {t('common.apply')}
                                                </Button>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="font-medium text-gray-900">
                                            €{formatPrice(totals?.p2b_markup_gross)}
                                        </span>
                                    )}
                                </div>
                                <div className="text-sm text-gray-500 flex justify-end">
                                    {showPricing ? (
                                        liveApproxOther !== null && (
                                            commissionMode === '€'
                                                ? `≈ ${liveApproxOther.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}% ${t('checkout.ofServicesTotal')}`
                                                : `≈ €${liveApproxOther.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${t('checkout.ofServicesTotal')}`
                                        )
                                    ) : (
                                        <span>(<span className="font-semibold">{totals?.p2b_commission_percentage ?? DEFAULT_P2B_COMMISSION_PERCENTAGE}%</span> {t('checkout.ofServicesTotal')})</span>
                                    )}
                                </div>
                            </div>

                            <div className="border-t border-gray-300"></div>
                            <div className="flex items-center justify-between">
                                <h5 className="text-gray-900">{t('checkout.total')}</h5>
                                <h5 className="text-gray-900">
                                    €{formatPrice(totals?.total)}
                                </h5>
                            </div>

                            {showPricing && (
                                <>
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-gray-900">{t('checkout.place2BeFee')}</span>
                                            <span className="font-medium text-gray-900">
                                                €{formatPrice(totals?.p2b_fee_amount)}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            (3% {t('checkout.ofTotal')})
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-gray-900">
                                                {t('checkout.yourEarnings')}
                                            </span>
                                            <span className="font-medium text-gray-900">
                                                €{formatPrice(totals?.td_fee_amount)}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            ({t('checkout.advisorFeeMinusP2BFee')})
                                        </div>
                                    </div>

                                </>
                            )}

                            {totals?.custom_fee > 0 && (
                                <>
                                    <div className="border-t border-gray-300"></div>
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-gray-900">{t('checkout.customFee')}</span>
                                            <span className="font-medium text-gray-900">
                                                €{formatPrice(totals?.custom_fee)}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            ({t('checkout.additionalFeeByAdmin')})
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Piano rate: solo per chi vende, non per il viaggiatore che consulta. */}
                {showPricing && (
                    <PaymentPlanCard
                        itineraryId={data.id}
                        itineraryTotal={totals?.total ?? 0}
                        departureDate={data.start_date}
                        /* Chi apre il builder e non e' un viaggiatore puo' gestire
                           l'itinerario, quindi puo' anche registrare un incasso a mano:
                           dal 02/09/2026 non e' piu' riservato all'admin, in cambio della
                           commissione a debito che l'operazione lascia dietro di se'. */
                        canMarkPaid={canMarkInstallmentPaid}
                        formatAmount={(value) => `€${formatPrice(value)}`}
                        formatDate={formatDate}
                    />
                )}

                </VerticalScrollableContentWithArrows>
            </div>
        </div>
    );
};

export default CheckoutStep;
